import type { ReactNode } from "react";

export type PlacementHeroProps = {
  eyebrow: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  icon: ReactNode;
  illustration?: ReactNode;
  actions?: ReactNode;
  badges?: ReactNode;
};

export const PlacementPageShell = ({
  children,
  contentClassName = "max-w-[1920px]",
}: {
  children: ReactNode;
  contentClassName?: string;
}) => (
  <main className="w-full px-4 py-5 sm:px-6 lg:px-8">
    <div className={`mx-auto ${contentClassName} rounded-[32px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_26px_70px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-slate-950/85 sm:p-6`}>
      <div className="space-y-5">
        {children}
      </div>
    </div>
  </main>
);

export const PlacementHero = ({
  eyebrow,
  title,
  description,
  icon,
  illustration,
  actions,
  badges,
}: PlacementHeroProps) => (
  <section className="relative z-20 overflow-visible rounded-[30px] border border-indigo-100/80 bg-gradient-to-r from-indigo-50/95 via-white to-sky-50/90 px-5 py-6 shadow-[0_22px_55px_rgba(15,23,42,0.10)] dark:border-indigo-500/25 dark:from-slate-950 dark:via-slate-900 dark:to-sky-950 sm:px-7">
    <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[34%] rounded-r-[30px] bg-gradient-to-l from-sky-100/75 via-sky-50/35 to-transparent lg:block dark:from-sky-900/20 dark:via-sky-950/10" />
    <div className="pointer-events-none absolute inset-y-0 right-8 hidden items-center justify-center text-[8rem] text-sky-300/45 lg:flex dark:text-sky-500/20">
      {illustration}
    </div>
    <div className="relative z-10 flex min-w-0 flex-col gap-5 lg:flex-row lg:items-center lg:pr-48">
      <div className="flex min-w-0 flex-1 items-center gap-5">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-indigo-200 to-sky-50 text-3xl text-indigo-700 shadow-lg dark:border-slate-900 dark:from-indigo-800 dark:to-sky-950 dark:text-indigo-100 sm:h-24 sm:w-24">
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.09em] text-slate-500 dark:text-slate-400">
            {eyebrow}
          </p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
            {title}
          </h1>
          {description ? (
            <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
              {description}
            </p>
          ) : null}
          {badges ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {badges}
            </div>
          ) : null}
        </div>
      </div>
      {actions ? <div className="shrink-0">{actions}</div> : null}
    </div>
  </section>
);

export const PlacementSection = ({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) => (
  <section
    className={`rounded-[26px] border border-slate-200/80 bg-white/90 shadow-sm dark:border-slate-700 dark:bg-slate-900/80 ${className}`}
  >
    {children}
  </section>
);

export const PlacementStat = ({
  label,
  value,
  icon,
}: {
  label: ReactNode;
  value: ReactNode;
  icon?: ReactNode;
}) => (
  <div className="rounded-2xl border border-indigo-100/70 bg-gradient-to-br from-white to-indigo-50/60 p-4 dark:border-indigo-500/25 dark:from-slate-900 dark:to-indigo-950/30">
    <div className="flex items-center justify-between gap-3 text-sm font-semibold text-indigo-600 dark:text-indigo-300">
      <span>{label}</span>
      {icon}
    </div>
    <div className="mt-2 text-2xl font-bold text-slate-950 dark:text-white">
      {value}
    </div>
  </div>
);
